import React, { FC, FormEvent, useId, useState } from 'react';
import {
  GUEST_NAME_MAX,
  normalizeGuestName,
  validateGuestName,
} from '../lib/names';

export interface NameFormProps {
  defaultName?: string;
  submitLabel: string;
  busyLabel?: string;
  onSubmit: (name: string) => Promise<void>;
  /** Error from the server, shown under the field. */
  serverError?: string | null;
  autoFocus?: boolean;
}

/** "What should we call you?" — the only thing a guest has to type. */
export const NameForm: FC<NameFormProps> = ({
  defaultName = '',
  submitLabel,
  busyLabel = 'Saving…',
  onSubmit,
  serverError,
  autoFocus = true,
}) => {
  const id = useId();
  const [name, setName] = useState(defaultName);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  const localError = touched ? validateGuestName(name) : null;
  const error = localError || serverError || null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (validateGuestName(name)) return;
    setBusy(true);
    try {
      await onSubmit(normalizeGuestName(name));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="st-stack" onSubmit={submit} noValidate>
      <div className="st-field">
        <label className="st-label" htmlFor={`${id}-name`}>
          Your name at the table
        </label>
        <input
          id={`${id}-name`}
          className="st-input"
          name="name"
          autoComplete="nickname"
          autoCapitalize="words"
          enterKeyHint="go"
          maxLength={GUEST_NAME_MAX + 8}
          placeholder="e.g. Simran"
          value={name}
          autoFocus={autoFocus}
          aria-invalid={!!error}
          aria-describedby={`${id}-hint ${error ? `${id}-err` : ''}`}
          onChange={e => setName(e.target.value)}
          onBlur={() => setTouched(true)}
          disabled={busy}
        />
        <span id={`${id}-hint`} className="st-hint">
          2–24 characters. Friends see this name. No email or password needed.
        </span>
        {error && (
          <span id={`${id}-err`} className="st-error" role="alert">
            {error}
          </span>
        )}
      </div>
      <button
        type="submit"
        className="st-btn st-btn--primary st-btn--block"
        disabled={busy}
        aria-busy={busy}
      >
        {busy ? busyLabel : submitLabel}
      </button>
    </form>
  );
};
